import React, { useEffect, useState, useMemo } from 'react';
import '../../../styles/accessibility.css';
import { getPrefs, updatePrefs } from '../../../services/shared/preferencesService';
import {
  getEngagementSummary,
  getRealTimeMetrics,
  getBehavioralData,
  getConversionFunnels,
  getPerformanceMetrics,
  getUserSegments,
  getABTests,
  subscribe as subUserEngagement,
  trackEvent
} from '../../../services/bread/userEngagementService';
import { getRealTimeEvents } from '../../../services/bread/userEngagementService';
import {
  filter as eaFilter,
  computeKPIs as eaComputeKPIs,
  getSegments as eaGetSegments,
  subscribe as subEA,
  regenerate as eaRegenerate
} from '../../../services/bread/engagementAnalyticsService';
import { add as addAuditLog } from '../../../services/bread/auditLogService';
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, Legend, BarChart, Bar
} from 'recharts';

// Utility helpers
function formatNumber(n){ if(n==null) return '—'; if(n>999999) return (n/1000000).toFixed(1)+'M'; if(n>999) return (n/1000).toFixed(1)+'K'; return n.toLocaleString(); }
function percent(val, digits=1){ return (val*100).toFixed(digits)+'%'; }

const REFRESH_INTERVAL = 30000; // 30s real-time refresh

const BreadOverview = () => {
  const [rt, setRT] = useState(()=> getRealTimeMetrics());
  const [uxTick, setUxTick] = useState(0); // simple rerender trigger for subscriptions
  const [eaStateTick, setEaStateTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [events, setEvents] = useState([]);
  const initialPrefs = getPrefs();
  const [showBenchmarks, setShowBenchmarks] = useState(initialPrefs.engagementShowBenchmarks !== false);
  const [esgOverlay, setEsgOverlay] = useState(initialPrefs.engagementEsgOverlay !== false);
  useEffect(()=>{ updatePrefs({ engagementShowBenchmarks: showBenchmarks }); }, [showBenchmarks]);
  useEffect(()=>{ updatePrefs({ engagementEsgOverlay: esgOverlay }); }, [esgOverlay]);

  // Subscribe to both data sources to keep derived metrics fresh
  useEffect(()=>{
    const unsubA = subUserEngagement(()=> setUxTick(t=> t+1));
    const unsubB = subEA(()=> setEaStateTick(t=> t+1));
    setLoading(false);
    trackEvent('page_view', { page: 'bread_overview' });
    addAuditLog({ user: 'system', action: 'page_access', target: 'bread_overview', status: 'success' });
    const intv = setInterval(()=> {
      setRT(getRealTimeMetrics());
      setEvents(getRealTimeEvents(40));
    }, REFRESH_INTERVAL);
    setEvents(getRealTimeEvents(40));
    return ()=> { unsubA(); unsubB(); clearInterval(intv); };
  }, []);

  const summary7 = useMemo(()=> getEngagementSummary(7) || {}, [uxTick]);
  const summary14 = useMemo(()=> getEngagementSummary(14) || {}, [uxTick]);
  const delta = useMemo(()=>{
    // naive period-over-period last 7 vs prev 7
    const recent = summary7;
    const priorFactor = 7/14; // approximate proportion if using combined 14 summary
    if(!summary14.totalUsers) return {};
    const baselineUsers = summary14.totalUsers - recent.totalUsers;
    const baselineSessions = summary14.totalSessions - recent.totalSessions;
    const d = (curr, base)=> base ? ((curr-base)/base) : 0;
    return {
      users: d(recent.totalUsers, baselineUsers),
      sessions: d(recent.totalSessions, baselineSessions),
      duration: d(recent.avgSessionDuration, summary14.avgSessionDuration - recent.avgSessionDuration),
      engagement: d(recent.avgEngagementScore, summary14.avgEngagementScore - recent.avgEngagementScore),
      bounce: d(recent.avgBounceRate, summary14.avgBounceRate - recent.avgBounceRate)
    };
  }, [summary7, summary14]);

  const behavioral = useMemo(()=> getBehavioralData(), [uxTick]);
  const timeseries = behavioral.slice(-30).map(d=> ({
    date: d.date.slice(5),
    sessions: d.sessions,
    engagement: d.engagementScore,
    bounceRate: d.bounceRate
  }));

  // --- Anomaly Detection (simple z-score on sessions) ---
  const anomalies = useMemo(()=>{
    if(timeseries.length < 8) return [];
    const values = timeseries.map(t=> t.sessions);
    const mean = values.reduce((a,b)=> a+b,0)/values.length;
    const variance = values.reduce((a,b)=> a + Math.pow(b-mean,2),0)/values.length;
    const sd = Math.sqrt(variance)||1;
    return timeseries
      .map((t,i)=> ({ ...t, z: (t.sessions-mean)/sd, idx:i }))
      .filter(x=> Math.abs(x.z) >= 2.0);
  }, [timeseries]);

  // Engagement Analytics service (secondary dataset)
  const eaItems = useMemo(()=> eaFilter({}), [eaStateTick]);
  const eaKPIs = useMemo(()=> eaComputeKPIs(eaItems.slice(-7)), [eaItems]);
  const segments = useMemo(()=> getUserSegments().slice(0,5), [uxTick]);
  const funnels = useMemo(()=> getConversionFunnels(), [uxTick]);
  const perf = useMemo(()=> getPerformanceMetrics(), [uxTick]);
  const ab = useMemo(()=> getABTests(), [uxTick]);

  const primaryFunnel = funnels[0];

  function diffBadge(val){
    if(val==null || isNaN(val)) return null;
    const cls = val>0 ? 'text-success' : val<0 ? 'text-danger' : 'text-muted';
    const arrow = val>0 ? '▲' : val<0 ? '▼' : '■';
    return <span className={cls + ' small ms-1'}>{arrow} {Math.abs(val*100).toFixed(1)}%</span>;
  }

  function regenerate(){
    setRegenerating(true);
    eaRegenerate(30);
    setTimeout(()=> setRegenerating(false), 600);
  }

  function exportOverview(){
    const payload = {
      generatedAt: new Date().toISOString(),
      summary7,
      summary14,
      delta,
      realTime: rt,
      segments,
      performance: perf,
      funnel: primaryFunnel,
      abTests: ab,
      events
    };
    const blob = new Blob([JSON.stringify(payload,null,2)], { type:'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'bread-overview.json'; a.click(); URL.revokeObjectURL(url);
  }

  if(loading){
    return (
      <div className="container-fluid py-4">
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3">Loading Bread overview…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h2 className="mb-1">Bread Overview</h2>
          <p className="text-muted mb-0">Comprehensive data analytics & engagement intelligence (Bread Department)</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <button className="btn btn-outline-secondary btn-sm" onClick={regenerate} disabled={regenerating}>
            {regenerating ? 'Regenerating…' : 'Synthetic Refresh'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={()=> setRT(getRealTimeMetrics())}>Real-time Sync</button>
          <button className="btn btn-outline-primary btn-sm" onClick={exportOverview}>Export JSON</button>
          <button className="btn btn-outline-success btn-sm" onClick={()=> exportCSV(timeseries)}>Export CSV</button>
          <div className="form-check form-switch mb-0 small">
            <input className="form-check-input" type="checkbox" id="benchSwitch" checked={showBenchmarks} onChange={()=> setShowBenchmarks(v=> !v)} />
            <label className="form-check-label" htmlFor="benchSwitch">Benchmarks</label>
          </div>
          <div className="form-check form-switch mb-0 small">
            <input className="form-check-input" type="checkbox" id="esgSwitch" checked={esgOverlay} onChange={()=> setEsgOverlay(v=> !v)} />
            <label className="form-check-label" htmlFor="esgSwitch">ESG Overlay</label>
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="row g-3 mb-3">
        <KPI title="Active Users" value={formatNumber(rt.activeUsers)} delta={delta.users} accent="primary" tooltip="Current simulated active users" />
        <KPI title="Sessions (7d)" value={formatNumber(summary7.totalSessions)} delta={delta.sessions} accent="info" />
        <KPI title="Avg Session" value={Math.round(summary7.avgSessionDuration/60)||0 + 'm'} delta={delta.duration} accent="warning" />
        <KPI title="Engagement Score" value={summary7.avgEngagementScore} delta={delta.engagement} accent="success" />
        <KPI title="Bounce Rate" value={percent(summary7.avgBounceRate||0,0)} delta={-delta.bounce} accent="danger" invert />
        <KPI title="EA Satisfaction" value={eaKPIs.satisfaction} accent="secondary" />
      </div>

      <div className="row g-4">
        {/* Timeseries */}
        <div className="col-lg-7">
          <div className="card shadow-sm h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">30‑Day Sessions & Engagement</h5>
              <small className="text-muted">Combined sources</small>
            </div>
            <div className="card-body">
              <div style={{height:220, marginBottom: '0.75rem'}}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={timeseries} anomalies={anomalies} />
                </ResponsiveContainer>
              </div>
              {showBenchmarks && (
                <div className="mt-2 small text-muted">
                  <strong>Benchmarks:</strong> Sessions target +5% WoW • Engagement score ≥ 70 • Bounce ≤ 45%
                </div>
              )}
              {!!anomalies.length && (
                <div className="mt-1 small text-danger">
                  {anomalies.length} anomaly{anomalies.length>1?' points':''} detected (|z| ≥ 2.0)
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Funnel Snapshot */}
        <div className="col-lg-5">
          <div className="card shadow-sm h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Primary Funnel</h5>
              <small className="text-muted">{primaryFunnel?.name}</small>
            </div>
            <div className="card-body">
              {primaryFunnel ? primaryFunnel.steps.map((s,i)=>{
                const pct = Math.round(s.conversionRate*100);
                return (
                  <div key={i} className="mb-3">
                    <div className="d-flex justify-content-between small mb-1"><span>{s.name}</span><span>{formatNumber(s.users)} • {pct}%</span></div>
                    <div className="progress" style={{height:6}}>
                      <div className={"progress-bar bg-" + (pct>70?'success': pct>50?'warning':'danger')} style={{width: pct+'%'}} />
                    </div>
                  </div>
                );
              }) : <p className="text-muted small mb-0">No funnel data</p>}
              {esgOverlay && (
                <div className="alert alert-light border mt-3 py-2 small mb-0">
                  <strong>ESG Impact (simulated):</strong> Onboarding funnel aligned with accessibility & data minimization principles.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mt-1">
        {/* Segments */}
        <div className="col-xl-4">
          <div className="card shadow-sm h-100">
            <div className="card-header"><h6 className="mb-0">Top Segments</h6></div>
            <div className="card-body p-0">
              <ul className="list-group list-group-flush small">
                {segments.map((seg,i)=> (
                  <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                    <span className="d-flex align-items-center gap-2"><span style={{background:seg.color,width:10,height:10,borderRadius:2,display:'inline-block'}} />{seg.name}</span>
                    <span className="text-muted">{seg.size.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {/* Performance */}
        <div className="col-xl-4">
          <div className="card shadow-sm h-100">
            <div className="card-header"><h6 className="mb-0">Performance Metrics</h6></div>
            <div className="card-body p-0">
              <table className="table table-sm mb-0 align-middle">
                <thead className="table-light"><tr><th>Metric</th><th className="text-end">Value</th><th className="text-end">Target</th></tr></thead>
                <tbody>
                  {perf.slice(0,5).map((m,i)=>{
                    const ok = m.value <= m.target || m.trend==='improving';
                    return (
                      <tr key={i}>
                        <td>{m.metric}</td>
                        <td className="text-end"><span className={ok? 'text-success':'text-danger'}>{m.value}{m.unit}</span></td>
                        <td className="text-end text-muted small">{m.target}{m.unit}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* A/B Tests */}
        <div className="col-xl-4">
          <div className="card shadow-sm h-100">
            <div className="card-header d-flex justify-content-between align-items-center"><h6 className="mb-0">A/B Tests</h6><small className="text-muted">{ab.length} active</small></div>
            <div className="card-body p-0">
              <ul className="list-group list-group-flush small">
                {ab.map((t,i)=> (
                  <li key={i} className="list-group-item">
                    <div className="d-flex justify-content-between">
                      <strong>{t.name}</strong>
                      <span className={`badge ${t.status==='Running'?'bg-success':'bg-secondary'}`}>{t.status}</span>
                    </div>
                    <div className="mt-1 d-flex flex-wrap gap-2">
                      {t.variants.map((v,j)=> (
                        <span key={j} className="border rounded px-2 py-1 bg-light">{v.name}: {Math.round(v.conversions/Math.max(1,v.visitors)*100)}%</span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Correlation & Real-Time Events */}
      <div className="row g-4 mt-1">
        <div className="col-lg-7">
          <div className="card shadow-sm h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Engagement vs Bounce (Scatter Approx)</h6>
              <small className="text-muted">Last 30 days</small>
            </div>
            <div className="card-body" style={{minHeight:260}}>
              <div className="row row-cols-5 g-2 small">
                {timeseries.slice(-30).map((d,i)=>{
                  const score = d.engagement;
                  const bounce = d.bounceRate;
                  const color = score>75? '#16a34a' : score>60? '#0d9488' : score>40? '#f59e0b' : '#dc2626';
                  const opacity = 1 - Math.min(0.8, bounce);
                  return (
                    <div key={i} className="col">
                      <div className="border rounded p-1 text-center" style={{background: color, opacity, color:'#fff'}} title={`Eng: ${score} Bounce: ${(bounce*100).toFixed(0)}%`}>{score}</div>
                    </div>
                  );
                })}
              </div>
              {esgOverlay && <p className="small text-muted mt-2 mb-0">Lower bounce & higher engagement correlate with improved ethical usage patterns (simulated).</p>}
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card shadow-sm h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Recent Real-Time Events</h6>
              <small className="text-muted">{events.length}</small>
            </div>
            <div className="card-body p-0" style={{maxHeight:300, overflow:'auto'}}>
              {events.length===0 ? <div className="p-3 small text-muted">No events</div> : (
                <ul className="list-group list-group-flush small">
                  {events.map(e=> (
                    <li key={e.id} className="list-group-item d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{e.name}</strong>
                        <div className="text-muted">{e.properties.page || e.properties.section || '—'}</div>
                      </div>
                      <span className="text-nowrap text-muted ms-2" style={{fontSize:'11px'}}>{new Date(e.timestamp).toLocaleTimeString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

function KPI({ title, value, delta, accent='primary', invert=false, tooltip }){
  const val = value==null ? '—' : value;
  let cls = 'text-muted';
  if(delta!=null){
    const good = invert ? delta<0 : delta>0;
    cls = good ? 'text-success' : delta===0 ? 'text-muted' : 'text-danger';
  }
  return (
    <div className="col-6 col-md-4 col-xl-2">
      <div className={`border rounded shadow-sm p-3 h-100 position-relative bg-white` } title={tooltip || ''}>
        <div className="small text-uppercase text-muted fw-semibold mb-1">{title}</div>
  <div className={`h4 mb-0 fw-semibold text-accent-${accent}`}>{val}</div>
        {delta!=null && !isNaN(delta) && (
          <div className={`small mt-1 ${cls}`}>{delta>0 ? '▲' : delta<0 ? '▼' : '■'} {Math.abs(delta*100).toFixed(1)}%</div>
        )}
      </div>
    </div>
  );
}

function ComposedChart({ data, anomalies = [] }){
  return (
    <LineChart data={data}>
      <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
      <XAxis dataKey="date" tick={{fontSize:11}} />
      <YAxis yAxisId="left" tick={{fontSize:11}} width={50} />
      <YAxis yAxisId="right" orientation="right" tick={{fontSize:11}} width={40} />
      <Tooltip />
      <Legend />
      <Line yAxisId="left" type="monotone" dataKey="sessions" stroke="#6366f1" strokeWidth={2} dot={false} name="Sessions" />
      <Line yAxisId="right" type="monotone" dataKey="engagement" stroke="#10b981" strokeWidth={2} dot={false} name="Engagement" />
      <Line yAxisId="right" type="monotone" dataKey="bounceRate" stroke="#ef4444" strokeWidth={1} strokeDasharray="4 4" dot={false} name="Bounce" />
      {anomalies.map(a=> (
        <circle key={a.idx} cx={/* approximate: find index position via layout later (fallback omitted) */ undefined} />
      ))}
    </LineChart>
  );
}

// Simple CSV exporter
function exportCSV(rows){
  if(!rows || !rows.length) return;
  const header = ['date','sessions','engagement','bounceRate'];
  const body = rows.map(r=> [r.date, r.sessions, r.engagement, r.bounceRate].join(','));
  const csv = [header.join(','), ...body].join('\n');
  const blob = new Blob([csv], { type:'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href=url; a.download='bread-timeseries.csv'; a.click(); URL.revokeObjectURL(url);
}

export default BreadOverview;